// src/utils/axiosInstance.js
import axios from 'axios';

/**
 * Base URL logic:
 * 1) If REACT_APP_API_BASE (or legacy REACT_APP_API) is set at build time, use it.
 * 2) If running on localhost, default to http://localhost:5050/api (dev).
 * 3) Otherwise (production on DigitalOcean App Platform), use relative '/api'
 *    so requests are same-origin and don't need CORS.
 */
function resolveApiBase() {
  const envBase =
    (process.env.REACT_APP_API_BASE || process.env.REACT_APP_API || '').trim();

  if (envBase) {
    // strip trailing slashes
    return envBase.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:5050/api';
    }
  }

  // Production default: same-origin route to backend
  return '/api';
}

const API_BASE = resolveApiBase();
export const API_BASE_URL = API_BASE; // alias
export const BASE_URL = API_BASE;     // keep old name working

// For images like /uploads/... ; when API_BASE is '/api', PUBLIC_BASE becomes '' which is fine:
// '' + '/uploads/foo.jpg' => '/uploads/foo.jpg' on the same origin.
export const PUBLIC_BASE = API_BASE.replace(/\/api$/, '');

const instance = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
});

/* ---------------- Bootstrap auth from localStorage ---------------- */
(function bootstrapAuth() {
  try {
    const t = localStorage.getItem('token');
    if (t) instance.defaults.headers.common.Authorization = `Bearer ${t}`;
  } catch {}
})();

/* ---------------- Interceptor: auth + FormData handling ----------- */
instance.interceptors.request.use((config) => {
  try {
    // Keep auth header in sync before each request
    const t = localStorage.getItem('token');
    if (t) {
      (config.headers || (config.headers = {})).Authorization = `Bearer ${t}`;
    } else if (config.headers) {
      delete config.headers.Authorization;
    }
  } catch {}

  // If sending FormData, let the browser set the multipart boundary.
  const isFormData =
    typeof FormData !== 'undefined' && config?.data instanceof FormData;

  if (isFormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  } else {
    // For plain objects, default to JSON if caller didn't set anything.
    if (
      config.headers &&
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof Blob) &&
      !(config.data instanceof ArrayBuffer) &&
      !('Content-Type' in config.headers) &&
      !('content-type' in config.headers)
    ) {
      config.headers['Content-Type'] = 'application/json';
    }
  }

  return config;
});

/* ---------------- Auth helpers ---------------- */
export const setAuthToken = (token) => {
  try {
    if (token) {
      localStorage.setItem('token', token);
      instance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete instance.defaults.headers.common.Authorization;
    }
  } catch {}
};

export const clearAuth = () => setAuthToken(null);

export default instance;
